import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import * as d3 from 'd3';
import axios from 'axios';
import { translate } from '../../../utils/i18n';

/**
 * 방위별 조류 출현현황을 실시간으로 표시하는 플롯 컴포넌트
 * @param {object} props
 * @param {string} props.language - 언어 설정
 */
const RadarDirectionChart = ({ language }) => {
  const svgRef = useRef(null);
  const [bboxData, setBboxData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // API에서 바운딩 박스 데이터 가져오기
  useEffect(() => {
    const fetchBoundingBoxData = async () => {
      try {
        // 가장 최근의 탐지 데이터 가져오기
        const response = await axios.get('/api/detections/filtered/', {
          params: {
            date_from: new Date().toISOString().split('T')[0],
            date_to: new Date().toISOString().split('T')[0],
            sort_by: 'date_desc',
            per_page: 20 // 충분한 데이터를 가져와서 각 카메라별 최신 데이터 찾기
          }
        });

        if (response.data && response.data.status === 'success' && response.data.detections) {
          const detections = response.data.detections;
          
          // 각 카메라별로 가장 최근 탐지만 선택
          const camera1Detection = detections.find(d => d.camera_id === 1);
          const camera2Detection = detections.find(d => d.camera_id === 2);
          const camera3Detection = detections.find(d => d.camera_id === 3);
          
          const selectedDetections = [camera1Detection, camera2Detection, camera3Detection].filter(d => d);
          
          // 선택된 탐지에 대한 바운딩 박스 정보 가져오기
          const allBboxData = [];
          
          for (const detection of selectedDetections) {
            try {
              const bbResponse = await axios.get(`/api/detection/bb-info/${detection.detection_id}/`);
              
              if (bbResponse.data && bbResponse.data.status === 'success' && bbResponse.data.bb_info) {
                // 바운딩 박스 정보에 카메라 ID 추가
                const bboxesWithCamera = bbResponse.data.bb_info.map(bb => ({
                  ...bb,
                  camera_id: detection.camera_id,
                  detection_time: detection.detection_time,
                  bbox_id: `${detection.detection_id}_${bb.record_id}`,
                  // 백분율로 변환된 크기 정보
                  bbox_width: (bb.bb_right - bb.bb_left) * 100,
                  bbox_height: (bb.bb_bottom - bb.bb_top) * 100
                }));
                
                allBboxData.push(...bboxesWithCamera);
              }
            } catch (error) {
              console.error(`바운딩 박스 정보 가져오기 오류 (ID: ${detection.detection_id}):`, error);
            }
          }
          
          setBboxData(allBboxData);
          console.log(`방위별 출현현황 데이터: ${allBboxData.length}개 (카메라별 최신 탐지만 표시)`);
        }
      } catch (error) {
        console.error('탐지 데이터 가져오기 오류:', error);
      } finally {
        setLoading(false);
      }
    };

    // 초기 데이터 로드
    fetchBoundingBoxData();
    
    // 1초마다 업데이트 (실시간성 향상)
    const interval = setInterval(fetchBoundingBoxData, 1000);

    return () => clearInterval(interval);
  }, []);

  // 초기 배경 그리기 (한 번만 실행)
  useEffect(() => {
    if (initialized || loading || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const margin = { top: 10, right: 30, bottom: 10, left: 10 };
    const width = 280 - margin.left - margin.right;
    const height = 220 - margin.top - margin.bottom;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) * 0.45; // 원 크기 조정

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // 배경 원 그리기
    g.append('circle')
      .attr('cx', centerX)
      .attr('cy', centerY)
      .attr('r', radius)
      .style('fill', 'none')
      .style('stroke', 'rgba(122, 139, 160, 0.3)')
      .style('stroke-width', 1);

    // 섹터 경계선 그리기 (3개의 경계선: 0도, 120도, 240도)
    const boundaries = [0, 120, 240];
    boundaries.forEach(angle => {
      const angleRad = (angle - 90) * Math.PI / 180;
      g.append('line')
        .attr('x1', centerX)
        .attr('y1', centerY)
        .attr('x2', centerX + radius * Math.cos(angleRad))
        .attr('y2', centerY + radius * Math.sin(angleRad))
        .style('stroke', 'rgba(122, 139, 160, 0.3)')
        .style('stroke-width', 1);
    });

    // 방위 표시
    const directions = [
      { angle: 0, label: 'N' },
      { angle: 45, label: 'NE' },
      { angle: 90, label: 'E' },
      { angle: 135, label: 'SE' },
      { angle: 180, label: 'S' },
      { angle: 225, label: 'SW' },
      { angle: 270, label: 'W' },
      { angle: 315, label: 'NW' }
    ];

    directions.forEach(dir => {
      const angleRad = (dir.angle - 90) * Math.PI / 180;
      const labelRadius = radius * 1.15;
      const x = centerX + labelRadius * Math.cos(angleRad);
      const y = centerY + labelRadius * Math.sin(angleRad);
      
      g.append('text')
        .attr('x', x)
        .attr('y', y)
        .style('font-size', '10px')
        .style('fill', '#7a8ba0')
        .style('text-anchor', 'middle')
        .style('dominant-baseline', 'middle')
        .text(dir.label);
    });

    // 거리 원 추가 (50m, 100m, 150m, 200m)
    const distanceCircles = [
      { distance: 50, r: 0.28, opacity: 0.15 },
      { distance: 100, r: 0.43, opacity: 0.12 },
      { distance: 150, r: 0.58, opacity: 0.08 },
      { distance: 200, r: 0.75, opacity: 0.05 }
    ];

    distanceCircles.forEach(circle => {
      g.append('circle')
        .attr('cx', centerX)
        .attr('cy', centerY)
        .attr('r', radius * circle.r)
        .style('fill', 'none')
        .style('stroke', 'rgba(122, 139, 160, ' + circle.opacity + ')')
        .style('stroke-width', 0.5);
    });

    // 플롯을 위한 그룹 생성
    g.append('g').attr('class', 'plot-group');

    setInitialized(true);
  }, [loading, initialized]);

  // 플롯만 업데이트
  useEffect(() => {
    if (!initialized || !svgRef.current || loading) return;

    const margin = { top: 10, right: 30, bottom: 10, left: 10 };
    const width = 280 - margin.left - margin.right;
    const height = 220 - margin.top - margin.bottom;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) * 0.45; // 원 크기 조정

    // 카메라별 색상 및 각도 범위
    const cameraConfig = {
      1: { color: '#3E6D9C', minAngle: 240, maxAngle: 360 }, // 북서쪽
      2: { color: '#5E8C61', minAngle: 0, maxAngle: 120 }, // 북동쪽
      3: { color: '#AF7AB3', minAngle: 120, maxAngle: 240 }  // 남쪽
    };

    // SVG 선택 및 플롯 그룹 선택
    const svg = d3.select(svgRef.current);
    const plotGroup = svg.select('.plot-group');

    if (plotGroup.empty()) return;

    // 바운딩 박스 데이터를 극좌표로 변환
    const processedData = bboxData.map(bb => {
      const config = cameraConfig[bb.camera_id];
      if (!config) return null;

      // 카메라의 각도 범위 내에서 랜덤하게 배치
      const angleRange = config.maxAngle - config.minAngle;
      const angle = config.minAngle + Math.random() * angleRange;
      const angleRad = (angle - 90) * Math.PI / 180;
      
      // 반경은 바운딩 박스 크기에 따라 조정
      // 바운딩 박스 면적 계산
      const bboxArea = (bb.bbox_width || 0) * (bb.bbox_height || 0);
      console.log(`Bird: ${bb.class_name}, Camera: ${bb.camera_id}, BBox Area: ${bboxArea.toFixed(1)}%²`);
      
      // 거리 계산 (바운딩 박스 크기 기반)
      // 작은 바운딩 박스도 더 가까이 배치
      let distance;
      if (bboxArea >= 2500) {
        // 초대형 박스 (5-10m)
        distance = 0.1;
      } else if (bboxArea >= 2000) {
        // 매우 큰 박스 (10-15m)
        distance = 0.15;
      } else if (bboxArea >= 1500) {
        // 큰 박스 (15-20m)
        distance = 0.2;
      } else if (bboxArea >= 1000) {
        // 중대형 박스 (20-30m)
        distance = 0.25;
      } else if (bboxArea >= 700) {
        // 중형 박스 (30-40m)
        distance = 0.3;
      } else if (bboxArea >= 500) {
        // 중소형 박스 (40-50m)
        distance = 0.35;
      } else if (bboxArea >= 350) {
        // 소형 박스 (50-70m)
        distance = 0.4;
      } else if (bboxArea >= 200) {
        // 작은 박스 (70-100m)
        distance = 0.45;
      } else if (bboxArea >= 150) {
        // 매우 작은 박스 (100-120m) - 121.9%²가 여기 해당
        distance = 0.5;
      } else if (bboxArea >= 100) {
        // 극히 작은 박스 (120-150m)
        distance = 0.55;
      } else if (bboxArea >= 80) {
        // 아주 작은 박스 (150-170m) - 95.6%²가 여기 해당
        distance = 0.6;
      } else if (bboxArea >= 60) {
        // 미세한 박스 (170-190m)
        distance = 0.65;
      } else if (bboxArea >= 40) {
        // 극미세 박스 (190-210m)
        distance = 0.7;
      } else if (bboxArea >= 25) {
        // 매우 극미세 박스 (210-240m)
        distance = 0.75;
      } else if (bboxArea >= 15) {
        // 거의 점 (240-270m) - 19.5%²가 여기 해당
        distance = 0.8;
      } else if (bboxArea >= 10) {
        // 점 수준 (270-300m)
        distance = 0.85;
      } else {
        // 극점 수준 (300m+)
        distance = 0.9;
      }
      
      const r = radius * distance;
      
      return {
        id: bb.bbox_id,
        x: centerX + r * Math.cos(angleRad),
        y: centerY + r * Math.sin(angleRad),
        cameraId: bb.camera_id,
        color: config.color,
        species: bb.class_name,
        bbox_width: bb.bbox_width || 0,
        bbox_height: bb.bbox_height || 0
      };
    }).filter(d => d !== null);

    // 기존 점들 업데이트/제거/추가
    const circles = plotGroup.selectAll('circle')
      .data(processedData, d => d.id);

    // 제거
    circles.exit()
      .transition()
      .duration(300)
      .style('opacity', 0)
      .remove();

    // 추가
    const newCircles = circles.enter()
      .append('circle')
      .attr('cx', d => d.x)
      .attr('cy', d => d.y)
      .attr('r', 0)
      .style('fill', d => d.color)
      .style('opacity', 0)
      .style('cursor', 'pointer');

    // 업데이트 (기존 + 신규)
    circles.merge(newCircles)
      .transition()
      .duration(300)
      .attr('cx', d => d.x)
      .attr('cy', d => d.y)
      .attr('r', 3)
      .style('fill', d => d.color)
      .style('opacity', 0.8);

    // 이벤트 핸들러 추가
    plotGroup.selectAll('circle')
      .on('mouseover', function(event, d) {
        // 툴팁 표시
        const tooltip = d3.select('body').append('div')
          .attr('class', 'bbox-tooltip')
          .style('position', 'absolute')
          .style('padding', '10px')
          .style('background', 'rgba(0, 0, 0, 0.8)')
          .style('color', 'white')
          .style('border-radius', '4px')
          .style('pointer-events', 'none')
          .style('opacity', 0)
          .style('font-size', '12px');

        tooltip.transition()
          .duration(200)
          .style('opacity', .9);

        const cameraDirection = {
          1: '북서쪽',
          2: '북동쪽',
          3: '남쪽'
        };

        // 거리 추정
        const area = d.bbox_width * d.bbox_height;
        let estimatedDistance;
        if (area >= 2500) estimatedDistance = '5-10m';
        else if (area >= 2000) estimatedDistance = '10-15m';
        else if (area >= 1500) estimatedDistance = '15-20m';
        else if (area >= 1000) estimatedDistance = '20-30m';
        else if (area >= 700) estimatedDistance = '30-40m';
        else if (area >= 500) estimatedDistance = '40-50m';
        else if (area >= 350) estimatedDistance = '50-70m';
        else if (area >= 200) estimatedDistance = '70-100m';
        else if (area >= 150) estimatedDistance = '100-120m';
        else if (area >= 100) estimatedDistance = '120-150m';
        else if (area >= 70) estimatedDistance = '150-180m';
        else if (area >= 50) estimatedDistance = '180-200m';
        else if (area >= 30) estimatedDistance = '200-250m';
        else if (area >= 15) estimatedDistance = '250-300m';
        else estimatedDistance = '300m+';

        tooltip.html(`
          <strong>${d.species || '미확인'}</strong><br/>
          카메라: ${d.cameraId} (${cameraDirection[d.cameraId]})<br/>
          크기: ${d.bbox_width.toFixed(1)}% × ${d.bbox_height.toFixed(1)}%<br/>
          면적: ${area.toFixed(0)}%²<br/>
          추정 거리: ${estimatedDistance}
        `)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 28) + 'px');

        // 호버된 점 강조
        d3.select(this)
          .transition()
          .duration(100)
          .attr('r', 5)
          .style('opacity', 1);
      })
      .on('mouseout', function() {
        d3.selectAll('.bbox-tooltip').remove();
        
        // 원래 크기로 복원
        d3.select(this)
          .transition()
          .duration(100)
          .attr('r', 3)
          .style('opacity', 0.8);
      });

  }, [bboxData, initialized, loading]);

  return (
    <Box sx={{ 
      width: '100%',
      height: '100%',
      backgroundColor: '#050A18',
      borderRadius: 2,
      p: 1,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* 제목 */}
      <Typography 
        variant="subtitle2" 
        fontWeight="bold" 
        sx={{ 
          mb: 1,
          color: 'white',
          textShadow: '0 0 5px rgba(255,255,255,0.5)'
        }}
      >
        {translate('방위별 출현현황', 'Directional Detection Status', language)}
      </Typography>
      
      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          flex: 1 
        }}>
          <CircularProgress size={30} sx={{ color: '#4a9eff' }} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg 
            ref={svgRef} 
            width="280" 
            height="220"
            style={{ width: '280px', height: '220px' }}
          />
        </Box>
      )}
    </Box>
  );
};

export default RadarDirectionChart; 